// Uygulama içi sekme şeridi (SEM-6724). AppShell'de, içeriğin üstünde durur.
//
// Şerit tarayıcıyla YARIŞMAZ: etkin sekmenin yolu URL'in kendisidir. Her sekme
// gerçek bir bağlantıdır, bu yüzden orta tıklama ve "yeni sekmede aç" hâlâ
// tarayıcı sekmesi açar — şerit bir seçenek ekler, bir seçeneği almaz.
//
// Kural ve saklama gerekçeleri store/tabs.ts başlığındadır.

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { GripVertical, Pin, X } from "lucide-react";

import { HOME_PATH, menuLabelKeyForPath, useTabsStore, type AppTab } from "@/store/tabs";

interface DropTarget { id: string; edge: "before" | "after" }
interface TabDrag {
  id: string;
  pointerId: number;
  handle: HTMLButtonElement;
  startX: number;
  startY: number;
  x: number;
  y: number;
  started: boolean;
  frame: number | null;
}

/**
 * Kayıt ekranı kendi başlığını bildirir (personel adı, talep numarası).
 * Bildirilmezse sekme manifestten türetilen ata başlığı okur.
 */
export function useTabTitle(title: string | null | undefined): void {
  const { pathname } = useLocation();
  const setActiveTitle = useTabsStore((s) => s.setActiveTitle);
  useEffect(() => {
    if (title !== null && title !== undefined && title !== "") setActiveTitle(title);
  }, [pathname, title, setActiveTitle]);
}

export function TabStrip(): React.ReactElement | null {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = useTabsStore((s) => s.tabs);
  const activeId = useTabsStore((s) => s.activeId);
  const syncLocation = useTabsStore((s) => s.syncLocation);
  const closeTab = useTabsStore((s) => s.closeTab);
  const togglePin = useTabsStore((s) => s.togglePin);
  const moveTab = useTabsStore((s) => s.moveTab);
  const stripRef = useRef<HTMLElement>(null);
  const drag = useRef<TabDrag | null>(null);
  const movedId = useRef<string | null>(null);
  const [dragView, setDragView] = useState<{ id: string; target: DropTarget | null } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const moveHintId = useId();
  const activeLink = useRef<HTMLAnchorElement>(null);
  const restoreFocus = useRef(false);
  useEffect(() => {
    if (movedId.current !== null) {
      // Etkin olmayan bir sekmeyi taşımak odağı etkin sekmeye kaçırmamalı.
      const item = [...(stripRef.current?.querySelectorAll<HTMLElement>("[data-tab-id]") ?? [])]
        .find(element => element.dataset["tabId"] === movedId.current);
      item?.querySelector<HTMLButtonElement>("[data-tab-move]")?.focus({ preventScroll: true });
      item?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
      movedId.current = null;
      return;
    }
    activeLink.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    if (restoreFocus.current) { activeLink.current?.focus(); restoreFocus.current = false; }
  }, [activeId, tabs]);

  const path = `${location.pathname}${location.search}`;
  useEffect(() => {
    const closeId: unknown = (location.state as { __novemCloseTab?: unknown } | null)?.__novemCloseTab;
    // Geçmiş girdisi bu durumu SONSUZA kadar taşır. Tarayıcının ileri tuşu
    // aynı girdiye dönerse etki yeniden koşar. `closeTab` kayıp kimlikte
    // etkisizdir, ama odak geri yüklemesi değildir: kapanış olmadan odağı
    // şeride çekerdi. Bu yüzden sekmenin GERÇEKTEN durduğunu önce ölçeriz.
    if (typeof closeId === "string" && useTabsStore.getState().tabs.some((t) => t.id === closeId)) {
      restoreFocus.current = true;
      closeTab(closeId);
    }
    syncLocation(path);
  }, [path, location.state, closeTab, syncLocation]);

  useEffect(() => () => {
    if (drag.current?.frame !== null && drag.current?.frame !== undefined) cancelAnimationFrame(drag.current.frame);
  }, []);

  if (tabs.length === 0) return null;

  const label = (tab: AppTab): string => {
    if (tab.title !== null) return tab.title;
    const key = menuLabelKeyForPath(tab.path);
    return key === null ? t("tabs.untitled") : t(key);
  };

  const reorder = (id: string, target: DropTarget): void => {
    const before = useTabsStore.getState().tabs;
    moveTab(id, target.id, target.edge);
    const after = useTabsStore.getState().tabs;
    if (before === after) return;
    movedId.current = id;
    const index = after.findIndex(tab => tab.id === id);
    setAnnouncement(t("tabs.moved", { title: label(after[index]!), position: index + 1, total: after.length }));
  };

  const dropTargetAt = (sourceId: string, x: number, y: number): DropTarget | null => {
    const strip = stripRef.current;
    if (strip === null) return null;
    const bounds = strip.getBoundingClientRect();
    if (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) return null;
    const others = [...strip.querySelectorAll<HTMLElement>("[data-tab-id]")]
      .filter(item => item.dataset["tabId"] !== sourceId);
    const before = others.find(item => {
      const box = item.getBoundingClientRect();
      return x < box.left + box.width / 2;
    });
    const target = before ?? others.at(-1);
    return target === undefined ? null : { id: target.dataset["tabId"]!, edge: before ? "before" : "after" };
  };

  const showDropTarget = (current: TabDrag): void => {
    const target = dropTargetAt(current.id, current.x, current.y);
    setDragView(previous => previous?.id === current.id && previous.target?.id === target?.id && previous.target?.edge === target?.edge
      ? previous : { id: current.id, target });
  };

  // Dar şeritte kenarda bekletmek görünmeyen sekmelere eriştirir. Kaydırma
  // yalnız gerçek sürüklemede çalışır, bırakma/iptalde tek kare bile kalmaz.
  const scrollWhileDragging = (): void => {
    const current = drag.current;
    const strip = stripRef.current;
    if (current === null || !current.started || strip === null) return;
    current.frame = null;
    const box = strip.getBoundingClientRect();
    if (current.y < box.top || current.y > box.bottom || current.x < box.left || current.x > box.right) return;
    const delta = current.x < box.left + 32 ? -10 : current.x > box.right - 32 ? 10 : 0;
    const previous = strip.scrollLeft;
    strip.scrollLeft += delta;
    if (strip.scrollLeft !== previous) {
      showDropTarget(current);
      current.frame = requestAnimationFrame(scrollWhileDragging);
    }
  };

  const cancelDrag = (): void => {
    const current = drag.current;
    drag.current = null;
    if (current?.frame !== null && current?.frame !== undefined) cancelAnimationFrame(current.frame);
    if (current?.handle.hasPointerCapture(current.pointerId)) current.handle.releasePointerCapture(current.pointerId);
    setDragView(null);
  };

  // Etkin sekme ancak gezinme KABUL edilince kapanır: kapatmayı gezinmenin
  // durumuna iliştiririz, böylece kaydedilmemiş bir form gezinmeyi bloklarsa
  // sekme de yerinde kalır. Son sekme kapanırken gidecek komşu yoktur; ana
  // sayfaya gideriz ve oradaki `syncLocation` yeni önizlemeyi açar.
  const onClose = (id: string): void => {
    const remaining = tabs.filter((tab) => tab.id !== id);
    // Boşalan şerit önce gezinir. Etkinlik durumuna bakmadan: etkin kimlik
    // bozuk bir sessionStorage kaydından sonra null olabilir ve o dalda
    // doğrudan kapatmak şeridi gezinme gelene kadar boş bırakırdı.
    if (remaining.length === 0) {
      // Zaten ana sayfadaysak gezinmek kapatmayı GERİ ALIR: aynı adres
      // `syncLocation`'ı tetikler ve sekme sabitlemesi silinmiş olarak
      // yeniden doğar. Kullanıcı "kapat" tıklayıp "sabitlemeyi kaldır"
      // görürdü. Burada doğrudan kapatırız ve şerit boş kalır; sonraki
      // gezinme yeni önizlemeyi açar.
      if (path === HOME_PATH) { closeTab(id); return; }
      void navigate(HOME_PATH, { state: { __novemCloseTab: id } });
      return;
    }
    if (id !== activeId) { restoreFocus.current = true; closeTab(id); return; }
    const index = tabs.findIndex((tab) => tab.id === id);
    const neighbour = remaining[Math.min(index, remaining.length - 1)]!;
    void navigate(neighbour.path, { state: { __novemCloseTab: id } });
  };

  return (
    // ARIA `tab`/`tablist` DEĞİL. O desen ok tuşlarıyla gezinme ve bir
    // `tabpanel` ister; şeritte sekmeler gerçek adreslerdir, panel yoktur.
    // Rolü takmak, desenin klavye sözünü yazmadan vermek olurdu.
    <nav
      ref={stripRef}
      aria-label={t("tabs.aria_label")}
      className="flex shrink-0 items-stretch gap-1.5 overflow-x-auto border-b border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-3 py-1.5"
    >
      <span id={moveHintId} className="sr-only">{t("tabs.move_hint")}</span>
      <span role="status" className="sr-only">{announcement}</span>
      {tabs.map((tab, index) => {
        const active = tab.id === activeId;
        const targetEdge = dragView?.target?.id === tab.id ? dragView.target.edge : null;
        return (
          <span
            key={tab.id}
            data-tab-id={tab.id}
            data-drop-edge={targetEdge ?? undefined}
            className={`relative flex shrink-0 max-w-[220px] items-center gap-1 rounded-[var(--radius-lg)] border px-2 py-1 text-[13px] ${dragView?.id === tab.id ? "opacity-50" : ""} ${
              targetEdge === "before" ? "before:absolute before:-left-1 before:inset-y-0 before:w-0.5 before:bg-[var(--accent)]" :
              targetEdge === "after" ? "after:absolute after:-right-1 after:inset-y-0 after:w-0.5 after:bg-[var(--accent)]" : ""
            } ${
              active
                ? "border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--fg-primary)]"
                : "border-transparent text-[var(--fg-secondary)] hover:bg-[var(--bg-surface)]"
            }`}
          >
            <button
              type="button"
              data-tab-move
              aria-label={t("tabs.move", { title: label(tab) })}
              aria-describedby={moveHintId}
              title={t("tabs.move_hint")}
              disabled={tabs.length < 2}
              className="flex h-8 w-6 shrink-0 touch-none select-none items-center justify-center rounded-[var(--radius-sm)] text-[var(--fg-secondary)] cursor-grab active:cursor-grabbing hover:bg-[var(--bg-subtle)] disabled:cursor-default disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              onPointerDown={event => {
                if (event.button !== 0 || drag.current !== null) return;
                event.currentTarget.focus({ preventScroll: true });
                event.currentTarget.setPointerCapture(event.pointerId);
                drag.current = { id: tab.id, pointerId: event.pointerId, handle: event.currentTarget, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, started: false, frame: null };
              }}
              onPointerMove={event => {
                const current = drag.current;
                if (current === null || current.pointerId !== event.pointerId) return;
                current.x = event.clientX;
                current.y = event.clientY;
                if (!current.started && Math.hypot(current.x - current.startX, current.y - current.startY) < 6) return;
                current.started = true;
                showDropTarget(current);
                if (current.frame === null) current.frame = requestAnimationFrame(scrollWhileDragging);
              }}
              onPointerUp={event => {
                const current = drag.current;
                if (current === null || current.pointerId !== event.pointerId) return;
                const target = current.started ? dropTargetAt(current.id, event.clientX, event.clientY) : null;
                cancelDrag();
                if (target !== null) reorder(current.id, target);
              }}
              onPointerCancel={cancelDrag}
              onLostPointerCapture={cancelDrag}
              onKeyDown={event => {
                if (event.key === "Escape") { cancelDrag(); return; }
                if (drag.current !== null || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
                const targetIndex = event.key === "ArrowLeft" ? index - 1 : event.key === "ArrowRight" ? index + 1 : event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : null;
                if (targetIndex === null) return;
                event.preventDefault();
                const target = tabs[targetIndex];
                if (target !== undefined) reorder(tab.id, { id: target.id, edge: targetIndex < index ? "before" : "after" });
              }}
            ><GripVertical size={14} aria-hidden="true" /></button>
            <Link
              ref={active ? activeLink : undefined}
              title={label(tab)}
              to={tab.path}
              aria-current={active ? "page" : undefined}
              className="min-w-0 py-1 truncate no-underline text-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              {label(tab)}
            </Link>
            {/* Durum ÜÇ işaretle okunur: dolgu, renk ve açı. Yalnız dolgu ile
                ayırmak yetmiyordu — 14 piksellik tek renk bir glifte dolu ile
                boş ayırt edilmiyor, kullanıcı düğmeyi göremediğini bildirdi.
                Açı renkten bağımsız bir işarettir ve renk körlüğünde de durur.
                `aria-pressed` YOKTUR: ad zaten değişiyor. İkisi birlikte ekran
                okuyucuda "sabitlemeyi kaldır, basılı" olur ve denetim her
                tıklamadan sonra yeni bir denetim gibi okunur. */}
            <button
              type="button"
              aria-label={t(tab.pinned ? "tabs.unpin" : "tabs.pin", { title: label(tab) })}
              title={t(tab.pinned ? "tabs.unpin" : "tabs.pin", { title: label(tab) })}
              onClick={() => togglePin(tab.id)}
              className={`shrink-0 rounded-[var(--radius-sm)] p-1.5 hover:bg-[var(--bg-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] ${
                tab.pinned
                  ? "text-[var(--accent)]"
                  : "text-[var(--fg-secondary)] hover:text-[var(--fg-primary)]"
              }`}
            >
              <Pin
                size={14}
                fill={tab.pinned ? "currentColor" : "none"}
                className={tab.pinned ? undefined : "-rotate-45"}
                aria-hidden="true"
              />
            </button>
            {/* Çarpı sabitlenmiş sekmede de, tek kalan sekmede de durur. Eski
                koşul `tabs.length > 1` idi ve tek kalan HER sekmede çarpıyı
                yok ediyordu; bu, "bu sekme kapatılamaz" demenin sessiz
                yoluydu. `title` de vardır: fare kullanıcısı hangi sekmeyi
                yok edeceğini görmeden tıklamamalıdır, geri alma yoktur. */}
            <button
              type="button"
              aria-label={t("tabs.close", { title: label(tab) })}
              title={t("tabs.close", { title: label(tab) })}
              onClick={(): void => onClose(tab.id)}
              className="shrink-0 rounded-[var(--radius-sm)] p-1.5 text-[var(--fg-muted)] hover:bg-[var(--bg-subtle)] hover:text-[var(--fg-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              <X size={13} aria-hidden="true" />
            </button>
          </span>
        );
      })}
    </nav>
  );
}
